import type { User, TierProgressResponse, Venue } from '@lunit/shared-types';
import type { CoreIdentity } from '@core/contexts/IdentityContext';

/**
 * CoreRuntime — контракт «тонкого входа» (shell) для общего ядра.
 *
 * Принцип (владелец, 2026-07-21 и 2026-10-04): PWA — лишь второй вход. Функционал мини-аппа и PWA одинаковый,
 * отличается только вход; экраны, маршруты (routes.tsx) и запросы — общие, в ядре. Клиента сервер берёт из
 * сессии входа (K-002), поэтому ни один экран не зависит от Telegram ID.
 * Всё, чем шеллы реально различаются, собрано в этом объекте:
 *   - как узнаётся заведение (Telegram start_param vs слаг в URL);
 *   - как аутентифицируются запросы (Bearer-токен initData vs httpOnly-cookie);
 *   - как грузится профиль клиента (/users/me?telegramId= vs /users/{id});
 *   - куда ведут ссылки «поделиться» (t.me мини-аппа vs сайт PWA) — в тот же вход, из которого поделились;
 *   - неизбежные свойства среды: requestContact и haptics Telegram, окно «поделиться» Telegram, выход из сессии.
 * Шелл вызывает initCoreRuntime() в main.tsx ДО первого рендера.
 */
export interface CoreCapabilities {
  /** Среда Telegram: haptics, requestContact и т.п. */
  telegram: boolean;
  /** Привязка телефона через Telegram requestContact (PWA не умеет и не нуждается). */
  requestContact: boolean;
  /** Мини-игра (страницы — в ядре, src/game; ходит по сессии входа, одинаково в мини-аппе и PWA). */
  game: boolean;
  /** Явный выход из сессии (в Telegram сессию завершать некуда). */
  logout: boolean;
  /** Уведомления в браузере (web-push): PWA — да; мини-апп — нет (уведомления приходят в Telegram). */
  webPush?: boolean;
}

/** Ссылки, которыми клиент делится. Ведут в тот же вход, из которого поделились: друг из Telegram открывает
 * мини-апп, друг без Telegram — PWA заведения. Форматы и их разбор — в links.ts. */
export interface ShareLinks {
  /** Приглашение друга с кодом клиента (referralCode профиля; у старой карточки Telegram — telegramId).
   * null — собрать нельзя (например, у заведения не настроен бот). */
  invite(venue: Venue, referralCode: string): string | null;
  /** Подарок другу по gift-link: deepLink — параметр мини-аппа, claimCode — код для PWA. */
  gift(venue: Venue, link: { deepLink: string; claimCode: string }): string | null;
}

export interface CoreRuntime {
  kind: 'telegram' | 'phone';
  /** Слаг заведения для X-Venue-Slug и VenueProvider. */
  getVenueSlug(): string;
  /** Заголовки аутентификации (mini-app: Bearer из /auth/telegram; PWA: пусто — cookie). */
  getAuthHeaders(): Record<string, string>;
  /** PWA: 'include' (кросс-поддоменная cookie). mini-app: undefined. */
  credentials?: RequestCredentials;
  /** Вызывается клиентом на HTTP 401 (PWA сбрасывает сессию → login). */
  onUnauthorized?(): void;
  /** mini-app: повторный вход (обмен свежей initData на сессию) на HTTP 401. true — сессия получена,
   * клиент повторит запрос один раз. Бэкенд определяет клиента только по сессии (K-002). PWA не задаёт. */
  reauthenticate?(): Promise<boolean>;
  /** Профиль клиента по identity из IdentityContext (единственный источник «кто я»);
   * null — идентичность не готова. */
  fetchProfile(identity: CoreIdentity): Promise<User | null>;
  /** Прогресс по уровням для identity. */
  fetchTierProgress(identity: CoreIdentity): Promise<TierProgressResponse | null>;
  capabilities: CoreCapabilities;
  links: ShareLinks;
  /** mini-app: окно Telegram «поделиться» (выбор чата). Нет — ядро использует Web Share API или копирование. */
  openShareDialog?(url: string, text: string): void;
  /** PWA: завершить cookie-сессию (кнопка «Выйти»). В Telegram выходить некуда — мини-апп не задаёт. */
  logout?(): Promise<void>;
  /** После удаления данных клиентом (K-025): PWA — выход на экран входа, мини-апп — закрыть окно Telegram.
   * Сессия на сервере уже отозвана; новый вход — новая карточка. */
  afterDataErased?(): void;
  /** mini-app: Telegram requestContact → сохранение телефона; отсутствует в PWA.
   * signedContact — подписанный Telegram `event.response` (строка contact=…&auth_date=…&hash=…):
   * сервер привязывает номер только по нему (K-001), phone из responseUnsafe — лишь для сверки. */
  requestPhoneContact?(onPhone: (phone: string, signedContact?: string) => Promise<void>): void;
}

let runtime: CoreRuntime | null = null;

export function initCoreRuntime(r: CoreRuntime): void {
  runtime = r;
}

export function coreRuntime(): CoreRuntime {
  if (!runtime) {
    throw new Error('CoreRuntime is not initialized — call initCoreRuntime() in the shell main.tsx');
  }
  return runtime;
}
